import { Component, OnInit, EventEmitter, Input, Output, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormBuilder, AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { PasswordState } from '../../models/password-state.model';
import { PasswordCriteria } from '../../models/password-criteria.model';
import { Account } from '../../models/account.model';

@Component({
  selector: 'app-set-password',
  templateUrl: './set-password.component.html',
  styleUrls: ['./set-password.component.scss']
})
export class SetPasswordComponent implements OnInit {
  public formGroup: FormGroup;
  public isDisplayed: boolean;
  private passwordCriteria: Map<PasswordCriteria, RegExp>;

  @Input() public stepAccount: Account;
  @Input() public passwordState: PasswordState;
  @Output() private passwordChanged: EventEmitter<PasswordState> = new EventEmitter();

  constructor(private formBuilder: FormBuilder) {}

  @ViewChild('passwordElement', { read: ElementRef, static: true }) input: ElementRef;

  private patternValidator(error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      const value = control.value;
      if (!value) {
        return null;
      }

      const numberRegex = this.passwordCriteria[PasswordCriteria.Number];
      const upperCaseRegex = this.passwordCriteria[PasswordCriteria.UpperCase];
      const lowerCaseRegex = this.passwordCriteria[PasswordCriteria.LowerCase];
      const specialSymbolCaseRegex = this.passwordCriteria[PasswordCriteria.SpecialSymbol];

      const valid =
        (numberRegex.test(value) && upperCaseRegex.test(value) && lowerCaseRegex.test(value)) ||
        (numberRegex.test(value) && upperCaseRegex.test(value) && specialSymbolCaseRegex.test(value)) ||
        (numberRegex.test(value) && lowerCaseRegex.test(value) && specialSymbolCaseRegex.test(value)) ||
        (upperCaseRegex.test(value) && lowerCaseRegex.test(value) && specialSymbolCaseRegex.test(value));

      return valid ? null : error;
    };
  }

  private currentPasswordValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      let value = control.value;
      if (!value) {
        return null;
      }
      value = value.toLowerCase();

      const isMatched = value.includes(this.stepAccount.firstName.toLowerCase()) || 
                      value.includes(this.stepAccount.lastName.toLowerCase()) ||
                      (this.stepAccount.companyName && value.includes(this.stepAccount.companyName.toLowerCase()));
      
      return isMatched ? { currentPasswordValidationError: true } : null;
    };
  }

  ngOnInit() {
    this.isDisplayed = false;
    this.setPasswordCriteriaMap();

    this.formGroup = this.createPasswordForm();
    this.formGroup.valueChanges.subscribe(val => {
      this.changedPassword();
    });
  }

  public get password() {
    return this.formGroup.get('password');
  }

  private setPasswordCriteriaMap(): void {
    this.passwordCriteria = new Map<PasswordCriteria, RegExp>();
    this.passwordCriteria[PasswordCriteria.Number] = /\d/;
    this.passwordCriteria[PasswordCriteria.UpperCase] = /[A-Z]/;
    this.passwordCriteria[PasswordCriteria.LowerCase] = /[a-z]/;
    this.passwordCriteria[PasswordCriteria.SpecialSymbol] = /[!@#$%&*():;<,>./?]/;
  }

  private configurePasswordInput() {
    this.passwordState.value = this.password.value;
  }

  private changedPassword() {
    this.configurePasswordInput();
    this.passwordState.isValid = this.formGroup.valid;
    this.passwordChanged.emit(this.passwordState);
  }

  private createPasswordForm(): FormGroup {
    return this.formBuilder.group({
      password: [
        this.passwordState.value,
        [Validators.required, Validators.minLength(8), this.patternValidator({ hasSecondaryCriteries: true }), this.currentPasswordValidator()]
      ]
    });
  }

  public isValidCase(caseName: string): boolean {
    return this.isValidCriteria(PasswordCriteria[caseName]);
  }

  private isValidCriteria(criteria: PasswordCriteria): boolean {
    return this.passwordCriteria[criteria].test(this.input.nativeElement.value);
  }

  public isInValidPasswordLength(): boolean {
    return (
      this.password.hasError('required') ||
      this.password.hasError('minlength')
    );
  }

  public passwordUpdated(): void {
    this.changePasswordDiscretes(PasswordCriteria.UpperCase);
    this.changePasswordDiscretes(PasswordCriteria.LowerCase);
    this.changePasswordDiscretes(PasswordCriteria.Number);
    this.changePasswordDiscretes(PasswordCriteria.SpecialSymbol);
  }

  private changePasswordDiscretes(discrete: PasswordCriteria): void {
    if (this.isValidCriteria(discrete)) {
      if (!this.passwordState.criteriaDiscretes[discrete]) {
        this.passwordState.validSecondaryCriteriesCounter++;
        this.passwordState.criteriaDiscretes[discrete] = true;
      }
    } else {
      if (this.passwordState.criteriaDiscretes[discrete]) {
        this.passwordState.validSecondaryCriteriesCounter--;
        this.passwordState.criteriaDiscretes[discrete] = false;
      }
    }
  }

  public onShowPasswordIconClick(): void {
    this.isDisplayed = !this.isDisplayed;
    this.input.nativeElement.type = this.isDisplayed ? 'text' : 'password';
  }

  public get isFollowCriteriasValid(): boolean {
    return this.passwordState.validSecondaryCriteriesCounter >= 3;
  }

  public isPasswordDicreteValid(discreteId: number): boolean {
    return this.passwordState.validSecondaryCriteriesCounter >= discreteId;
  }
}
